import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QueryObserverResult } from '@tanstack/query-core';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, filter, Observable, switchMap, take } from 'rxjs';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { Station } from '../../models/location/station';
import { LocationDataService } from '../../services/location-data.service';
import { FormattingService } from '../../services/formatting.service';
import { VirtualizationSettings } from '@progress/kendo-angular-dropdowns';

@Component({
    selector: 'app-stations-filter',
    templateUrl: './stations-filter.component.html',
    styleUrls: ['./stations-filter.component.scss'],
    imports: [AsyncPipe, LabelModule, DropDownsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StationsFilterComponent),
            multi: true,
        },
    ],
})
export class StationsFilterComponent implements ControlValueAccessor, OnInit {
    private locationDataService = inject(LocationDataService);
    formattingService = inject(FormattingService);
    private cdr = inject(ChangeDetectorRef);

    public stationsQueryResults$?: Observable<
        QueryObserverResult<Station[], Error>
    >;

    public isDisabled: boolean = false;
    public value: Station[] = [];
    public virtual: VirtualizationSettings = { itemHeight: 28 };

    private onChange: (value: Station[]) => void = () => {};
    private onTouched: () => void = () => {};

    private statesProvincesSubject = new BehaviorSubject<string[]>([]);

    @Input() set statesProvinces(value: string[]) {
        this.statesProvincesSubject.next(value ?? []);
    }

    writeValue(value: Station[]): void {
        this.value = value ?? [];
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: Station[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    handleValueChange(value: Station[]): void {
        this.value = value;
        this.onChange(value);
    }

    handleBlur(): void {
        this.onTouched();
    }

    ngOnInit(): void {
        this.stationsQueryResults$ = this.locationDataService.dataUpdated$.pipe(
            filter((updated) => updated),
            take(1),
            switchMap(() => this.statesProvincesSubject),
            switchMap(
                (statesProvinces) =>
                    this.locationDataService.getFilterStations(
                        false,
                        'Railcar',
                        '',
                        '',
                        statesProvinces,
                    ).result$,
            ),
        );
    }
}
